import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import type { KsefInvoice } from './types';

/**
 * A synced invoice's `RodzajFaktury` as an outline badge — the read-side counterpart of
 * {@link DocumentKindBadge}, shared by the issued/received list and the detail page so both label a
 * document the same way.
 *
 * FA(3) has no type of its own for the margin scheme: a margin invoice is a `VAT` (standard) document
 * carrying `PMarzy`, so the label comes from the (type, margin) pair and reads "VAT marża" rather than
 * a bare "VAT". On the rare margin document that is *not* standard — a margin correction, say — the
 * type keeps its own label and the scheme rides beside it in a second badge.
 */
export function InvoiceTypeBadge({ inv }: { inv: Pick<KsefInvoice, 'invoiceType' | 'margin'> }) {
  const { t } = useTranslation();
  const type = inv.invoiceType;
  const margin = inv.margin ?? false;
  if (!type && !margin) return null;

  if (margin && (type == null || type === 'VAT_STANDARD')) {
    return (
      <Badge variant="outline" className="font-normal">
        {t('invoices.type.VAT_STANDARD_MARGIN')}
      </Badge>
    );
  }
  return (
    <>
      {type ? (
        <Badge variant="outline" className="font-normal">
          {t(`invoices.type.${type}`, { defaultValue: type })}
        </Badge>
      ) : null}
      {margin ? (
        <Badge variant="outline" className="font-normal">
          {t('invoices.margin.badge')}
        </Badge>
      ) : null}
    </>
  );
}
